import * as React from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ScrollArea,
  cn,
  type DocsFolder,
  type DocsPage,
} from "@siena-ai/design-system";
import { ChevronRight } from "lucide-react";

export interface PublicDocsSidebarProps {
  folders: DocsFolder[];
  pages: DocsPage[];
  activePageId?: string;
  activeFolderId?: string;
  onPageClick: (page: DocsPage) => void;
  pageHref?: (page: DocsPage) => string;
  className?: string;
  style?: React.CSSProperties;
}

function PageItem({
  page,
  activePageId,
  pageHref,
  onPageClick,
}: {
  page: DocsPage;
  activePageId?: string;
  pageHref?: (page: DocsPage) => string;
  onPageClick: (page: DocsPage) => void;
}) {
  const classes = cn(
    "block w-full text-left px-2 py-1 text-[13px] rounded-md transition-colors truncate",
    activePageId === page.id
      ? "text-foreground font-medium bg-muted/50"
      : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
  );
  const href = pageHref?.(page);
  return (
    <li>
      {href ? (
        <a
          href={href}
          onClick={(e) => {
            e.preventDefault();
            onPageClick(page);
          }}
          className={classes}
        >
          {page.title}
        </a>
      ) : (
        <button onClick={() => onPageClick(page)} className={classes}>
          {page.title}
        </button>
      )}
    </li>
  );
}

export const PublicDocsSidebar: React.FC<PublicDocsSidebarProps> = ({
  folders,
  pages,
  activePageId,
  activeFolderId,
  onPageClick,
  pageHref,
  className,
  style,
}) => {
  const folderPages = React.useMemo(() => {
    const map = new Map<string, DocsPage[]>();
    for (const f of folders) {
      const fp = pages
        .filter(
          (p) =>
            p.folderIds.includes(f.id) &&
            p.status === "published" &&
            p.visibility === "public"
        )
        .sort((a, b) => a.position - b.position);
      if (fp.length > 0) map.set(f.id, fp);
    }
    return map;
  }, [folders, pages]);

  const topLevelFolders = React.useMemo(
    () =>
      folders
        .filter((f) => !f.parentFolderId)
        .sort((a, b) => a.position - b.position),
    [folders]
  );

  const getSubFolders = React.useCallback(
    (parentId: string) =>
      folders
        .filter((f) => f.parentFolderId === parentId)
        .sort((a, b) => a.position - b.position),
    [folders]
  );

  const hasVisibleContent = React.useCallback(
    (folderId: string): boolean => {
      if (folderPages.has(folderId)) return true;
      return getSubFolders(folderId).some((sub) => folderPages.has(sub.id));
    },
    [folderPages, getSubFolders]
  );

  const visibleTopLevel = topLevelFolders.filter((f) =>
    hasVisibleContent(f.id)
  );

  // Auto-detect the active folder from the active page.
  const autoActiveFolderId = React.useMemo(() => {
    if (!activePageId) return null;
    const page = pages.find((p) => p.id === activePageId);
    return page?.folderIds[0] ?? null;
  }, [activePageId, pages]);

  // Parent of the active folder (for auto-expanding parent when a subfolder is active).
  const autoActiveParentFolderId = React.useMemo(() => {
    if (!autoActiveFolderId) return null;
    const folder = folders.find((f) => f.id === autoActiveFolderId);
    return folder?.parentFolderId ?? null;
  }, [autoActiveFolderId, folders]);

  // Controlled open state — preserves user toggles AND auto-opens the active folder.
  const [openFolders, setOpenFolders] = React.useState<Set<string>>(
    () => new Set<string>()
  );

  // Whenever the active folder changes, ensure it (and its parent) are open.
  // We only ADD to the set — never remove — so user's manual expansions persist.
  React.useEffect(() => {
    setOpenFolders((prev) => {
      const next = new Set(prev);
      let changed = false;
      if (autoActiveFolderId && !next.has(autoActiveFolderId)) {
        next.add(autoActiveFolderId);
        changed = true;
      }
      if (autoActiveParentFolderId && !next.has(autoActiveParentFolderId)) {
        next.add(autoActiveParentFolderId);
        changed = true;
      }
      if (activeFolderId && !next.has(activeFolderId)) {
        next.add(activeFolderId);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [autoActiveFolderId, autoActiveParentFolderId, activeFolderId]);

  const toggleFolder = React.useCallback((id: string, open: boolean) => {
    setOpenFolders((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  return (
    <div className={cn("shrink-0 overflow-y-auto", className)} style={style}>
      <ScrollArea className="h-full">
        <nav className="pt-3.5 pb-5 px-3">
          <div className="space-y-0.5">
            {visibleTopLevel.map((col) => {
              const fp = folderPages.get(col.id) ?? [];
              const subFolders = getSubFolders(col.id).filter((sub) =>
                folderPages.has(sub.id)
              );
              const isOpen = openFolders.has(col.id);

              return (
                <Collapsible
                  key={col.id}
                  open={isOpen}
                  onOpenChange={(o) => toggleFolder(col.id, o)}
                >
                  <CollapsibleTrigger className="flex items-center w-full px-2 py-1 text-[13px] font-medium rounded-md hover:bg-muted/50 transition-colors">
                    <span className="truncate">{col.name}</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ul className="ml-2 pl-2 border-l border-border/50 space-y-0.5 py-0.5 min-w-0">
                      {fp.map((page) => (
                        <PageItem
                          key={page.id}
                          page={page}
                          activePageId={activePageId}
                          pageHref={pageHref}
                          onPageClick={onPageClick}
                        />
                      ))}
                      {subFolders.map((sub) => {
                        const subPages = folderPages.get(sub.id) ?? [];
                        const isSubOpen = openFolders.has(sub.id);
                        return (
                          <li key={sub.id} className="min-w-0">
                            <Collapsible
                              open={isSubOpen}
                              onOpenChange={(o) => toggleFolder(sub.id, o)}
                            >
                              <CollapsibleTrigger className="flex items-center w-full px-2 py-1 text-[13px] font-medium text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/30 transition-colors group">
                                <ChevronRight className="h-3 w-3 mr-1 shrink-0 transition-transform group-data-[state=open]:rotate-90" />
                                <span className="truncate">{sub.name}</span>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <ul className="ml-1.5 pl-2 border-l border-border/30 space-y-0.5 py-0.5 min-w-0">
                                  {subPages.map((page) => (
                                    <PageItem
                                      key={page.id}
                                      page={page}
                                      activePageId={activePageId}
                                      pageHref={pageHref}
                                      onPageClick={onPageClick}
                                    />
                                  ))}
                                </ul>
                              </CollapsibleContent>
                            </Collapsible>
                          </li>
                        );
                      })}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        </nav>
      </ScrollArea>
    </div>
  );
};
